import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { API, BRAND } from "../lib/api";

const QUICK = [
  { label: "Book a Ride", to: "/booking" },
  { label: "Request a Quote", to: "/contact?subject=quote" },
  { label: "Our Services", to: "/services" },
  { label: "Photoshoot Service", to: "/contact?subject=photoshoot" },
  { label: "Signature XL Service", to: "/contact?subject=quote&vehicle=signature-xl" },
  { label: "Fleet", to: "/fleet" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact Us", to: "/contact" },
];

export default function Concierge() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", text: "Welcome to Lume Enterprise. How may I assist you today?" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgs]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    setBusy(true);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q, session_id: sessionId }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const data = JSON.parse(line.slice(5).trim());
            if (data.delta) {
              setMsgs((m) => {
                const nm = [...m];
                nm[nm.length - 1] = { role: "assistant", text: (nm[nm.length - 1].text || "") + data.delta };
                return nm;
              });
            }
            if (data.session_id) setSessionId(data.session_id);
          } catch (e) { console.error("chat stream parse error", e); }
        }
      }
    } catch (e) {
      setMsgs((m) => {
        const nm = [...m];
        nm[nm.length - 1] = { role: "assistant", text: `I'm having trouble responding. Please call ${BRAND.phone}.` };
        return nm;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="concierge-toggle"
        aria-label="Open concierge chat"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center transition-transform hover:scale-105"
        style={{ background: "#0B0C0E", border: "1px solid rgba(212,175,55,0.5)", boxShadow: "0 10px 40px -10px rgba(212,175,55,0.35)" }}
      >
        {open ? <X className="text-[color:var(--lume-gold)]" size={22} /> : <MessageCircle className="text-[color:var(--lume-gold)]" size={22} />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-md rounded-2xl overflow-hidden flex flex-col"
             style={{ background: "#0F1013", border: "1px solid rgba(212,175,55,0.28)", height: "70vh", maxHeight: 620 }}
             data-testid="concierge-panel">
          <div className="p-5 border-b border-white/5">
            <div className="flex items-center justify-between">
              <div>
                <div className="eyebrow">Lume Concierge</div>
                <div className="font-serif text-lg text-white mt-1">AI Guest Assistant</div>
              </div>
              <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.6)]"/>
            </div>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-[color:var(--lume-gold)]/15 text-[color:var(--lume-gold-light)] border border-[color:var(--lume-gold)]/30"
                    : "bg-[#161A20] text-slate-200 border border-white/5"
                }`}>
                  {m.text || <span className="opacity-50">…</span>}
                </div>
              </div>
            ))}
          </div>

          <div className="px-5 pb-2 flex flex-wrap gap-2">
            {QUICK.map((q) => (
              <button key={q.label} onClick={() => { setOpen(false); nav(q.to); }} data-testid={`quick-${q.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-slate-300 hover:border-[color:var(--lume-gold)]/50 hover:text-white transition-colors">
                {q.label}
              </button>
            ))}
          </div>

          <div className="p-4 border-t border-white/5 flex items-center gap-2">
            <input value={input} onChange={(e)=>setInput(e.target.value)} onKeyDown={(e)=>e.key==='Enter'&&send()}
              placeholder="Ask the concierge…" className="input-lume" data-testid="concierge-input" />
            <button onClick={() => send()} disabled={busy || !input.trim()} className="btn-gold !py-3 !px-4" data-testid="concierge-send">
              <Send size={14}/>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
